import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { StarRating } from '@/components/ui/StarRating';
import { useToast } from '@/components/ui/Toast';

// Phase 7: rate-and-review a venue you've booked or held a membership at
// (the backend enforces that eligibility — see app/routers/reviews.py).
// Opened from MyBookings.jsx; pre-fills the customer's existing review for
// this venue, if any, so re-opening it is "edit your review" not "write a
// duplicate one".
export function ReviewModal({ academyId, venueName, open, onClose, onSaved }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !academyId) return;
    setLoading(true);
    api.get('/reviews/mine', { params: { academy_id: academyId } })
      .then(({ data }) => {
        if (data) {
          setRating(data.rating);
          setComment(data.comment || '');
        } else {
          setRating(0);
          setComment('');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, academyId]);

  const save = async () => {
    if (!rating) {
      toast({ title: 'Pick a star rating first', variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/reviews', { academy_id: academyId, rating, comment });
      toast({ title: 'Thanks for the review!', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Could not save review', description: err.response?.data?.detail || '', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-white/10 max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl uppercase">Rate {venueName}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="flex justify-center">
              <StarRating value={rating} size={32} interactive onChange={setRating} testIdPrefix="review-star" />
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional — how was it?"
              maxLength={1000}
              rows={4}
              className="w-full px-4 py-3 bg-transparent border border-white/20 text-white placeholder:text-white/30 focus:border-volt focus:outline-none resize-none"
              data-testid="review-comment"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={save} disabled={saving} className="flex-1" data-testid="submit-review">
                {saving ? 'Saving…' : 'Submit'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewModal;
